import { Button, Card, Group, Image, Modal, Select, Stack, Text, Title } from "@mantine/core";
import { useState } from "react";
import {
  EmptyState,
  ErrorState,
  FilterBar,
  LoadingState,
  PageHeader,
  useReturnFocus,
  useSearchParamState,
} from "@how/ui";
import {
  devotionalsByMonth,
  formatDevotionalDate,
  formatDevotionalMonth,
  publicMediaUrl,
} from "@how/website-core";
import { env } from "../../lib/env";
import { RichTextView } from "../../lib/RichTextView";
import { useTenantRecord } from "../tenant/queries";
import {
  isTodays,
  useChurchToday,
  useDevotionals,
  type PortalDevotional,
} from "./devotional-queries";

/**
 * Devotionals (ADR 0077, the member's half per ADR 0037): today's devotional in full, then the earlier ones by month to
 * search and read again, as the website's /devotional. Nothing here is personal: every member of the church reads the
 * same devotionals, and the database shows only those whose day has begun in the church's time zone.
 */
export function DevotionalsPage() {
  const devotionals = useDevotionals();
  const today = useChurchToday();
  const locale = useTenantRecord().data?.locale ?? "en-US";
  const [search, setSearch] = useSearchParamState("q");
  const [seriesFilter, setSeriesFilter] = useSearchParamState("series");
  const [open, setOpen] = useState<PortalDevotional | null>(null);
  const focus = useReturnFocus();

  const all = devotionals.data ?? [];
  const latest = all[0];
  const earlier = all.slice(1);
  const seriesOptions = [
    ...new Set(earlier.map((d) => d.series?.title).filter((t): t is string => t !== undefined)),
  ]
    .sort()
    .map((t) => ({ value: t, label: t }));
  const needle = search.trim().toLowerCase();
  const listed = earlier.filter(
    (d) =>
      (seriesFilter === "" || d.series?.title === seriesFilter) &&
      (needle === "" ||
        d.title.toLowerCase().includes(needle) ||
        (d.scripture.reference?.toLowerCase().includes(needle) ?? false) ||
        (d.series?.title.toLowerCase().includes(needle) ?? false)),
  );

  return (
    <>
      <PageHeader
        title="Devotionals"
        description="A passage, a reflection and a prayer from your church for each day."
      />
      {devotionals.isPending ? (
        <LoadingState rows={4} />
      ) : devotionals.isError ? (
        <ErrorState
          message={devotionals.error.message}
          onRetry={() => void devotionals.refetch()}
        />
      ) : latest === undefined ? (
        <EmptyState
          title="No devotionals yet"
          description="When your church publishes a devotional, you can read it here each day."
        />
      ) : (
        <Stack gap="xl">
          <Card withBorder radius="md" padding="lg" data-testid="todays-devotional">
            <Stack gap="md">
              <Text size="sm" fw={600} c="dimmed">
                {isTodays(latest, today) ? "Today's devotional" : "The latest devotional"} ·{" "}
                {formatDevotionalDate(latest.date, locale, "long")}
              </Text>
              <Title order={2}>{latest.title}</Title>
              <Byline devotional={latest} />
              <DevotionalBody devotional={latest} />
            </Stack>
          </Card>
          {earlier.length === 0 ? null : (
            <Stack gap="md">
              <Title order={2} size="h3">
                Earlier devotionals
              </Title>
              <FilterBar
                search={search}
                onSearchChange={(value) => {
                  setSearch(value === "" ? undefined : value);
                }}
                searchPlaceholder="Search by title, scripture or series"
                {...(seriesOptions.length === 0
                  ? {}
                  : {
                      filters: (
                        <Select
                          aria-label="Series"
                          placeholder="Every series"
                          clearable
                          data={seriesOptions}
                          value={seriesFilter === "" ? null : seriesFilter}
                          onChange={(value) => {
                            setSeriesFilter(value ?? undefined);
                          }}
                        />
                      ),
                    })}
              />
              {listed.length === 0 ? (
                <EmptyState
                  title="No devotionals match"
                  description="Try another word or series."
                />
              ) : (
                devotionalsByMonth(listed).map((group) => (
                  <Stack key={group.month} gap="xs" data-month={group.month}>
                    <Title order={3} size="h5">
                      {formatDevotionalMonth(`${group.month}-01`, locale)}
                    </Title>
                    {group.items.map((d) => (
                      <Card key={d.id} withBorder radius="md" padding="md" data-devotional={d.slug}>
                        <Group justify="space-between" wrap="nowrap" align="center">
                          <Stack gap={2} miw={0}>
                            <Text component="h4" fw={600} m={0}>
                              {d.title}
                            </Text>
                            <Text size="sm" c="dimmed">
                              {[
                                formatDevotionalDate(d.date, locale, "short"),
                                d.scripture.reference,
                                d.series?.title,
                              ]
                                .filter(Boolean)
                                .join(" · ")}
                            </Text>
                          </Stack>
                          <Button
                            variant="light"
                            aria-label={`Read ${d.title}`}
                            onClick={() => {
                              focus.remember();
                              setOpen(d);
                            }}
                          >
                            Read
                          </Button>
                        </Group>
                      </Card>
                    ))}
                  </Stack>
                ))
              )}
            </Stack>
          )}
        </Stack>
      )}
      {open === null ? null : (
        <Modal
          opened
          onClose={() => {
            setOpen(null);
            focus.restore();
          }}
          title={open.title}
          size="xl"
          closeButtonProps={{ "aria-label": "Close" }}
        >
          <Stack gap="md">
            <Text size="sm" c="dimmed">
              {formatDevotionalDate(open.date, locale, "long")}
            </Text>
            <Byline devotional={open} />
            <DevotionalBody devotional={open} />
          </Stack>
        </Modal>
      )}
    </>
  );
}

/** The scripture reference, the author and the series, on one line. */
function Byline({ devotional }: { readonly devotional: PortalDevotional }) {
  const parts = [
    devotional.scripture.reference,
    devotional.author === null ? null : `By ${devotional.author}`,
    devotional.series === null ? null : `Series: ${devotional.series.title}`,
  ].filter(Boolean);
  return parts.length === 0 ? null : <Text size="sm">{parts.join(" · ")}</Text>;
}

/** The image, the passage, the reflection and the prayer. */
function DevotionalBody({ devotional }: { readonly devotional: PortalDevotional }) {
  const { scripture } = devotional;
  const caption = [
    scripture.reference,
    scripture.version === null ? null : `(${scripture.version})`,
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <>
      {devotional.image === null ? null : (
        <Image
          src={publicMediaUrl(env.VITE_SUPABASE_URL, devotional.image.path)}
          alt={devotional.image.alt}
          radius="md"
          mah={360}
          fit="cover"
        />
      )}
      {scripture.text === null ? null : (
        <figure style={{ margin: 0 }}>
          <blockquote
            style={{
              margin: 0,
              paddingLeft: 12,
              borderLeft: "3px solid var(--how-border-default)",
            }}
          >
            <Text style={{ fontStyle: "italic" }}>{scripture.text}</Text>
          </blockquote>
          {caption === "" ? null : (
            <Text component="figcaption" size="sm" c="dimmed" mt={4}>
              {caption}
            </Text>
          )}
        </figure>
      )}
      {devotional.reflection === null ? null : <RichTextView nodes={devotional.reflection} />}
      {devotional.prayer === null ? null : (
        <Stack gap="xs">
          <Title order={3} size="h4">
            Pray
          </Title>
          <Text size="sm" style={{ whiteSpace: "pre-line" }}>
            {devotional.prayer}
          </Text>
        </Stack>
      )}
    </>
  );
}
